import { Box, Button, ButtonBase, IconButton, Popover, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close, ListAlt, PersonOutline } from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/he';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, EnrichedEventData, OrderData } from '../../api';
import TitledFullScreenDialog from '../../common/ui/TitledFullScreenDialog';
import { DEFAULT_ORDER_PAGE_COLOR, mb as mbTokens } from '../../common/theme/mbTokens';
import { useLanguage } from '../../common/GeneralUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPage } from '../../utils/AccountUtils';
import { CalendarItems } from '../calendarWrapper/CalendarWrapper';
import { SUMMARY_SLOT_SECONDS, SummarySlot } from '../utils/calendarSummary';
import { useOpenDayInAgenda } from '../utils/useOpenDayInAgenda';

export const formatSeconds = (seconds: number) => moment().startOf('day').add(seconds, 'seconds').format('HH:mm');

const formatRange = (start: number, duration: number) => `${formatSeconds(start)}–${formatSeconds(start + duration)}`;

type SummarySlotPopoverProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  date: string;
  slot: SummarySlot;
  items: CalendarItems;
  openOrder: (event: any, order: OrderData) => void;
  openEvent: (event: EnrichedEventData) => void;
};

export const SummarySlotPopover = ({
  anchorEl,
  open,
  onClose,
  date,
  slot,
  items,
  openOrder,
  openEvent,
}: SummarySlotPopoverProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = theme.direction === 'rtl';
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const openDayInAgenda = useOpenDayInAgenda();

  const ordersById = new Map((items.orders[date] || []).map((order) => [order.id, order]));
  const eventsById = new Map((items.events[date] || []).map((event) => [event.id, event]));
  const orderPageIdByItemId = new Map<string, string>();
  slot.itemIds.forEach((id) => {
    const item = ordersById.get(id) ?? eventsById.get(id);
    if (item) {
      orderPageIdByItemId.set(id, item.order_page_id);
    }
  });

  const title = `${moment(date).locale(language).format('dddd, D MMMM')} · ${formatRange(
    slot.start,
    SUMMARY_SLOT_SECONDS,
  )}`;
  const totals = t('calendar.summary.totals', { count: slot.count, persons: slot.persons });

  const onOpenInAgenda = () => {
    openDayInAgenda(date);
    onClose();
  };
  const onOrderClick = (order: OrderData) => {
    onClose();
    openOrder({ currentTarget: anchorEl }, order);
  };
  const onEventClick = (event: EnrichedEventData) => {
    onClose();
    openEvent(event);
  };

  const rowSx = {
    width: '100%',
    minHeight: isPhone ? 52 : 40,
    px: 2,
    py: 0.5,
    display: 'grid',
    gridTemplateColumns: isPhone ? '1fr auto' : '92px 1fr auto minmax(40px, auto)',
    alignItems: 'center',
    columnGap: 1,
    textAlign: 'start',
    '&:hover': { bgcolor: 'mb.ground' },
  };
  const mutedText = { fontSize: '13px', color: 'text.secondary' };

  const renderOrderRow = (order: OrderData) => {
    const range = formatRange(order.time ?? 0, order.duration);
    const orderNumber = order.order_number ? `#${order.order_number}` : '';
    const persons = (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25, ...mutedText }}>
        <span dir="ltr">{order.persons}</span>
        <PersonOutline sx={{ fontSize: 13 }} />
      </Box>
    );
    if (isPhone) {
      return (
        <ButtonBase key={order.id} sx={rowSx} onClick={() => onOrderClick(order)}>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontSize="15px" noWrap>
              {`${order.firstname} ${order.lastname}`}
            </Typography>
            <Typography dir="ltr" sx={{ ...mutedText, textAlign: 'start' }} noWrap>
              {[range, orderNumber].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {persons}
        </ButtonBase>
      );
    }
    return (
      <ButtonBase key={order.id} sx={rowSx} onClick={() => onOrderClick(order)}>
        <Typography dir="ltr" sx={{ ...mutedText, textAlign: 'start' }}>
          {range}
        </Typography>
        <Typography fontSize="14px" noWrap>
          {`${order.firstname} ${order.lastname}`}
        </Typography>
        <Typography dir="ltr" sx={mutedText}>
          {orderNumber}
        </Typography>
        {persons}
      </ButtonBase>
    );
  };

  const renderEventRow = (event: EnrichedEventData) => {
    const range = formatRange(event.time ?? 0, event.duration);
    const tickets = (
      <Typography dir="ltr" sx={{ ...mutedText, textAlign: 'end', whiteSpace: 'nowrap' }}>
        {`${event.used_tickets}/${event.tickets}`}
      </Typography>
    );
    if (isPhone) {
      return (
        <ButtonBase key={event.id} sx={rowSx} onClick={() => onEventClick(event)}>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontSize="15px" noWrap>
              {t('calendar.joiningWorkshop')}
            </Typography>
            <Typography dir="ltr" sx={{ ...mutedText, textAlign: 'start' }} noWrap>
              {range}
            </Typography>
          </Box>
          {tickets}
        </ButtonBase>
      );
    }
    return (
      <ButtonBase key={event.id} sx={rowSx} onClick={() => onEventClick(event)}>
        <Typography dir="ltr" sx={{ ...mutedText, textAlign: 'start' }}>
          {range}
        </Typography>
        <Typography fontSize="14px" noWrap>
          {t('calendar.joiningWorkshop')}
        </Typography>
        <span />
        {tickets}
      </ButtonBase>
    );
  };

  const groups = (
    <Box>
      {slot.types.map((type) => {
        const orderPage = getOrderPage(account, type.orderPageId);
        return (
          <Box key={type.orderPageId} sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1, pb: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: orderPage?.color ?? DEFAULT_ORDER_PAGE_COLOR,
                }}
              />
              <Typography fontSize="14px" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                {orderPage?.label?.[language]}
              </Typography>
              <Typography dir="ltr" sx={{ ...mutedText, flexShrink: 0 }}>
                {`×${type.count} · ${type.persons}`}
              </Typography>
            </Box>
            {slot.itemIds
              .filter((id) => orderPageIdByItemId.get(id) === type.orderPageId)
              .map((id) => {
                const order = ordersById.get(id);
                if (order) {
                  return renderOrderRow(order);
                }
                const event = eventsById.get(id);
                return event ? renderEventRow(event) : null;
              })}
          </Box>
        );
      })}
    </Box>
  );

  if (isPhone) {
    return (
      <TitledFullScreenDialog title={title} open={open} close={onClose}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'mb.surface' }}>
          <Typography sx={{ ...mutedText, px: 2, py: 1.5 }}>{totals}</Typography>
          <Box sx={{ flex: 1 }}>{groups}</Box>
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              p: 2,
              bgcolor: 'mb.surface',
              borderTop: '1px solid',
              borderColor: 'mb.lineSoft',
              boxShadow: mbTokens.stickyShadow,
            }}>
            <Button fullWidth size="large" variant="outlined" startIcon={<ListAlt />} onClick={onOpenInAgenda}>
              {t('calendar.summary.openInAgenda')}
            </Button>
          </Box>
        </Box>
      </TitledFullScreenDialog>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      // MUI Popover does not mirror these in RTL: open towards the end side.
      anchorOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
      slotProps={{
        paper: {
          sx: { width: 380, maxHeight: 500, display: 'flex', flexDirection: 'column', bgcolor: 'mb.surface' },
        },
      }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, pb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize="17px" fontWeight={600}>
            {title}
          </Typography>
          <Typography sx={mutedText}>{totals}</Typography>
        </Box>
        <IconButton aria-label={t('close')} onClick={onClose} sx={{ width: 40, height: 40 }}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', borderTop: '1px solid', borderColor: 'mb.lineSoft' }}>
        {groups}
      </Box>
      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'mb.lineSoft' }}>
        <Button startIcon={<ListAlt />} onClick={onOpenInAgenda}>
          {t('calendar.summary.openInAgenda')}
        </Button>
      </Box>
    </Popover>
  );
};
